import { motion } from "framer-motion";
import { useMemo } from "react";

interface Particle {
  blurAmount: number;
  brightness: number;
  delay: number;
  duration: number;
  id: number;
  movementX: number;
  movementY: number;
  opacity: number;
  size: number;
  x: number;
  y: number;
}

const PARTICLE_COUNT = 250;
const PARTICLE_CONFIG = {
  size: { min: 0.3, max: 4.3 },
  opacity: { min: 0.15, max: 0.75 },
  duration: { min: 10, max: 35 },
  delay: { max: 8 },
  movementX: { range: 40 },
  movementY: { min: -30, max: -10 },
  blur: { min: 0, max: 2.5 },
  brightness: { min: 0.6, max: 1.4 },
} as const;

const visualRandom = (): number => {
  const array = new Uint32Array(1);
  self.crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

const visualRandomInRange = (min: number, max: number): number =>
  visualRandom() * (max - min) + min;

const getParticleTier = () => {
  const rand = visualRandom();
  if (rand < 0.3) {
    return {
      opacityMultiplier: 1.2,
      blur: visualRandomInRange(0, 0.5),
      brightness: visualRandomInRange(1.1, 1.4),
    };
  }
  if (rand < 0.6) {
    return {
      opacityMultiplier: 0.8,
      blur: visualRandomInRange(0.5, 1.5),
      brightness: visualRandomInRange(0.9, 1.1),
    };
  }

  return {
    opacityMultiplier: 0.5,
    blur: visualRandomInRange(1.5, 2.5),
    brightness: visualRandomInRange(0.6, 0.9),
  };
};

const generateParticles = (): Particle[] =>
  Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const tier = getParticleTier();
    const baseOpacity = visualRandomInRange(
      PARTICLE_CONFIG.opacity.min,
      PARTICLE_CONFIG.opacity.max,
    );

    return {
      id: i,
      x: visualRandom() * 100,
      y: visualRandom() * 100,
      size: visualRandomInRange(
        PARTICLE_CONFIG.size.min,
        PARTICLE_CONFIG.size.max,
      ),
      blurAmount: tier.blur,
      brightness: tier.brightness,
      opacity: Math.min(baseOpacity * tier.opacityMultiplier, 1),
      duration: visualRandomInRange(
        PARTICLE_CONFIG.duration.min,
        PARTICLE_CONFIG.duration.max,
      ),
      delay: visualRandom() * PARTICLE_CONFIG.delay.max,
      movementX: (visualRandom() - 0.5) * PARTICLE_CONFIG.movementX.range,
      movementY: visualRandomInRange(
        PARTICLE_CONFIG.movementY.min,
        PARTICLE_CONFIG.movementY.max,
      ),
    };
  });

export const Particles = () => {
  const particles = useMemo(() => generateParticles(), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full will-change-transform"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            background:
              "radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)",
            filter: `blur(${particle.blurAmount}px) brightness(${particle.brightness})`,
          }}
          animate={{
            opacity: [
              particle.opacity * 0.2,
              particle.opacity,
              particle.opacity * 0.2,
            ],
            y: [0, particle.movementY, 0],
            x: [0, particle.movementX, 0],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};
